import { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import ScheduleViewPreferencesWorkspace from '../schedule-settings/schedule-view-preferences-workspace';
import {
  fetchScheduleViewPreferences,
  updateScheduleViewPreferences,
  type PersonalThemeOptions,
  type ScheduleViewPreferences,
} from '../group-schedule/view-preferences-api';
import { getState, setState } from '../../../shared/services/switcherServices';

const primaryPresets = [
  { label: 'Violet', value: '171, 125, 241' },
  { label: 'Sky', value: '69, 184, 224' },
  { label: 'Rose', value: '231, 105, 182' },
  { label: 'Evergreen', value: '26, 139, 114' },
  { label: 'Ocean', value: '10, 106, 169' },
];

const currentThemeOptions = (): PersonalThemeOptions => {
  const state = getState();
  return {
    mode: state.dataThemeMode === 'dark' ? 'dark' : 'light',
    navigationLayout: state.dataNavLayout === 'horizontal' ? 'horizontal' : 'vertical',
    pageStyle: ['classic', 'modern'].includes(state.dataPageStyle) ? state.dataPageStyle : 'regular',
    width: state.dataWidth === 'boxed' ? 'boxed' : 'fullwidth',
    primaryRgb: state.colorPrimaryRgb || '171, 125, 241',
  };
};

const applyTheme = (theme: PersonalThemeOptions) => setState({
  dataThemeMode: theme.mode,
  dataHeaderStyles: theme.mode,
  dataMenuStyles: theme.mode,
  dataNavLayout: theme.navigationLayout,
  dataVerticalStyle: theme.navigationLayout === 'vertical' ? 'overlay' : '',
  dataNavStyle: theme.navigationLayout === 'horizontal' ? 'menu-click' : '',
  dataPageStyle: theme.pageStyle,
  dataWidth: theme.width,
  colorPrimaryRgb: theme.primaryRgb,
});

export default function PersonalDisplaySettingsPanel() {
  const [preferences, setPreferences] = useState<ScheduleViewPreferences | null>(null);
  const [theme, setTheme] = useState<PersonalThemeOptions>(currentThemeOptions);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetchScheduleViewPreferences(controller.signal)
      .then((loaded) => {
        setPreferences(loaded);
        if (loaded.themeOptions) {
          setTheme(loaded.themeOptions);
          applyTheme(loaded.themeOptions);
        }
      })
      .catch((reason) => {
        if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : 'Unable to load personal display preferences.');
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, []);

  const updateTheme = (next: PersonalThemeOptions) => {
    setTheme(next);
    applyTheme(next);
    setSuccess(null);
  };

  const save = async () => {
    if (!preferences) return;
    setSaving(true); setError(null); setSuccess(null);
    try {
      const saved = await updateScheduleViewPreferences({ ...preferences, themeOptions: theme });
      setPreferences(saved);
      setSuccess('Personal theme options saved to your CARES account.');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to save personal theme options.');
    } finally {
      setSaving(false);
    }
  };

  return <div className="d-flex flex-column gap-3">
    <ScheduleViewPreferencesWorkspace />
    <Card className="custom-card mb-0 border">
      <Card.Header><div className="card-title mb-0">Personal theme options</div></Card.Header>
      <Card.Body>
        {loading ? <div className="d-flex align-items-center gap-2"><Spinner size="sm" /> Loading theme preferences.</div> : <>
          {error ? <Alert variant="warning">{error}</Alert> : null}
          {success ? <Alert variant="success">{success}</Alert> : null}
          <Row className="g-4">
            <Col md={6}><Form.Group><Form.Label className="fw-semibold">Theme mode</Form.Label><div className="d-flex gap-3"><Form.Check type="radio" label="Light" checked={theme.mode === 'light'} onChange={() => updateTheme({ ...theme, mode: 'light' })} /><Form.Check type="radio" label="Dark" checked={theme.mode === 'dark'} onChange={() => updateTheme({ ...theme, mode: 'dark' })} /></div></Form.Group></Col>
            <Col md={6}><Form.Group><Form.Label className="fw-semibold">Navigation layout</Form.Label><div className="d-flex gap-3"><Form.Check type="radio" label="Vertical" checked={theme.navigationLayout === 'vertical'} onChange={() => updateTheme({ ...theme, navigationLayout: 'vertical' })} /><Form.Check type="radio" label="Horizontal" checked={theme.navigationLayout === 'horizontal'} onChange={() => updateTheme({ ...theme, navigationLayout: 'horizontal' })} /></div></Form.Group></Col>
            <Col md={6}><Form.Group><Form.Label className="fw-semibold">Page style</Form.Label><Form.Select value={theme.pageStyle} onChange={event => updateTheme({ ...theme, pageStyle: event.target.value as PersonalThemeOptions['pageStyle'] })}><option value="regular">Regular</option><option value="classic">Classic</option><option value="modern">Modern</option></Form.Select></Form.Group></Col>
            <Col md={6}><Form.Group><Form.Label className="fw-semibold">Page width</Form.Label><Form.Select value={theme.width} onChange={event => updateTheme({ ...theme, width: event.target.value as PersonalThemeOptions['width'] })}><option value="fullwidth">Full width</option><option value="boxed">Boxed</option></Form.Select></Form.Group></Col>
            <Col xs={12}><Form.Label className="fw-semibold d-block">Primary color</Form.Label><div className="d-flex flex-wrap gap-2">{primaryPresets.map(preset => <Button type="button" variant={theme.primaryRgb === preset.value ? 'primary' : 'light'} key={preset.value} onClick={() => updateTheme({ ...theme, primaryRgb: preset.value })}><span className="d-inline-block rounded-circle me-2" style={{ width: 12, height: 12, background: `rgb(${preset.value})` }} />{preset.label}</Button>)}</div></Col>
          </Row>
          <div className="mt-4"><Button type="button" onClick={() => void save()} disabled={saving || !preferences}>{saving ? 'Saving…' : 'Save personal theme'}</Button></div>
        </>}
      </Card.Body>
    </Card>
  </div>;
}
